import { saveAs } from 'file-saver';
import millify from 'millify';
import { Theme } from 'ui';
import { captureException } from '@shared/libs/sentry';
import { FiltersForm } from '@shared/types/forms';
import { getEndOfTodayDate, getNewDate, toISOString } from '@utils/dates';
import {
  extractFourHourBlockDateRange,
  extractFourHourBlockTimeRange,
  formatSpikeDateRange,
} from '@utils/datesToSpike';
import { numberToPercentage, removeDecimals } from '@utils/numbers';
import { isEmptyString } from '@utils/strings';
import {
  Case,
  GetCaseAggregateResultsResponse,
} from 'services/ClaimscoreApiService/Cases/types';
import { DashboardFilters } from 'services/ClaimscoreApiService/shared/types';

export const getSpikeColorByIndex = (theme: Theme) => [
  theme.palette.orange[600],
  theme.palette.violet[600],
  theme.palette.red[600],
];

const EndOfDayTime = { hour: 23, minute: 59, second: 59, millisecond: 0 };

export const mapDatesToTimezoneDates = (startDate = '', endDate = '') => {
  if (isEmptyString(startDate) || isEmptyString(endDate))
    return { startDateUTC: '', endDateUTC: '' };

  const startDateUTC = toISOString(new Date(startDate));
  const endDateUTC = getNewDate(endDate);
  endDateUTC.setUTCHours(
    EndOfDayTime.hour,
    EndOfDayTime.minute,
    EndOfDayTime.second,
    EndOfDayTime.millisecond,
  );
  return { startDateUTC, endDateUTC: toISOString(endDateUTC) };
};

export const getEffectiveDateRange = (
  selectedCase: Case | undefined,
  today: string,
  filters: { startDate?: string; endDate?: string } = {},
) => {
  const configured = selectedCase?.claimSubmissionStartDate;
  const actual = selectedCase?.firstClaimSubmissionDate;
  const configuredStartIsInFuture = configured && configured > today;

  let defaultStartDate: string | undefined;
  if (configuredStartIsInFuture) {
    defaultStartDate = today;
  } else {
    defaultStartDate =
      actual && configured && actual < configured ? actual : configured;
  }

  const configuredEnd = selectedCase?.claimSubmissionEndDate;
  const configuredEndIsInFuture = !configuredEnd || configuredEnd >= today;

  let defaultEndDate: string;
  if (!selectedCase || configuredEndIsInFuture) {
    defaultEndDate = today;
  } else if (
    selectedCase.lastClaimSubmissionDate !== undefined &&
    selectedCase.lastClaimSubmissionDate > configuredEnd!
  ) {
    defaultEndDate = selectedCase.lastClaimSubmissionDate;
  } else {
    defaultEndDate = configuredEnd!;
  }

  const startDate = filters.startDate || defaultStartDate;
  const endDate = filters.endDate || defaultEndDate || today;

  return { defaultStartDate, defaultEndDate, startDate, endDate };
};

export const mapFormToPayload = (
  filters: FiltersForm,
  selectedCase: Case,
): DashboardFilters => {
  const {
    deductionCodes,
    scoreRange,
    startDate,
    endDate,
    useRealTimeClaims,
    files,
    claimscoreResult,
    submissionType,
    accessType,
    documentationProvided,
    entityType,
  } = filters;

  const hasScoreRange = scoreRange && scoreRange.length > 0;
  const hasDeductionCode = deductionCodes && deductionCodes.length > 0;
  const excludeRealTime = useRealTimeClaims === false;
  const hasFiles = files && files.length > 0;
  const hasClaimscoreResult = claimscoreResult && claimscoreResult.length > 0;
  const hasSubmissionType = submissionType && submissionType.length > 0;
  const hasAccessType = accessType && accessType.length > 0;
  const hasDocumentationProvided = documentationProvided !== undefined;
  const hasEntityType = entityType && entityType.length > 0;

  const today = getEndOfTodayDate();
  const { startDate: effectiveStart, endDate: effectiveEnd } =
    getEffectiveDateRange(selectedCase, today, { startDate, endDate });
  const { startDateUTC, endDateUTC } = mapDatesToTimezoneDates(
    effectiveStart ?? '',
    effectiveEnd,
  );

  return {
    dateRange: {
      startDate: startDateUTC,
      endDate: endDateUTC,
    },
    scoreRange: hasScoreRange
      ? {
          min: Number(scoreRange?.[0]),
          max: Number(scoreRange?.[1]),
        }
      : undefined,
    deductionCodes: hasDeductionCode ? deductionCodes : undefined,
    useRealTimeClaims: !excludeRealTime,
    fileIDs: hasFiles ? files : undefined,
    claimscoreResult: hasClaimscoreResult ? claimscoreResult : undefined,
    submissionType: hasSubmissionType ? submissionType : undefined,
    accessType: hasAccessType ? accessType : undefined,
    documentationProvided: hasDocumentationProvided
      ? documentationProvided
      : undefined,
    entityType: hasEntityType ? entityType : undefined,
  };
};

interface Spike {
  spikeID: string;
  title: string;
  totalClaims: number;
  vsAverage: number;
  borderColor: string;
  claimData: { key: string; value: string; color?: string }[];
  blocks: { title: string; info: { value: string; link: string }[] }[];
  date?: string;
  subdateRange?: string;
  timeInterval?: string;
}

export const keysToParse = [
  'scoreRange',
  'deductionCodes',
  'files',
  'claimscoreResult',
  'accessType',
];

export const decodeSearchParams = (searchParams: URLSearchParams) =>
  [...searchParams.entries()].reduce((acc, [key, val]) => {
    if (val === 'true' || val === 'false') {
      return {
        ...acc,
        [key]: val === 'true',
      };
    }
    if (!keysToParse.includes(key)) {
      return {
        ...acc,
        [key]: val,
      };
    }
    try {
      return {
        ...acc,
        [key]: val.includes(',') ? val.split(',') : [val],
      };
    } catch (error) {
      captureException(error);
      return {
        ...acc,
        [key]: undefined,
      };
    }
  }, {});

export const mapAggregateToSpikes = (
  dataToSpike: GetCaseAggregateResultsResponse,
  theme: Theme,
): Spike[] => {
  const { buckets, timeInterval } = dataToSpike;
  const spikeBuckets = buckets.filter((bucket) => bucket.isSpike);
  const totalBuckets = buckets.filter(
    (bucket) =>
      bucket.deductionCodesCount && bucket.deductionCodesCount.length > 0,
  );

  const averageClaims =
    buckets.reduce(
      (acc, curr) =>
        acc + curr.totalApproved + curr.totalPending + curr.totalRejected,
      0,
    ) / (totalBuckets.length || 1);

  const spikeColors = getSpikeColorByIndex(theme);
  return spikeBuckets.map((bucket, index) => {
    const {
      totalApproved: totalPass,
      totalPending: totalInsufficient,
      totalRejected: totalFail,
      sourceCount,
      deductionCodesCount,
      totalClaims,
    } = bucket;
    const passPercentage = numberToPercentage(totalPass, totalClaims);
    const insufficientPercentage = numberToPercentage(
      totalInsufficient,
      totalClaims,
    );
    const failPercentage = numberToPercentage(totalFail, totalClaims);

    const claimData = [
      {
        key: 'Score \u2267700',
        value: passPercentage === 0 ? 'N/A' : `${passPercentage}%`,
        color: theme.palette.primary.main,
      },
      {
        key: 'Score <700',
        value: failPercentage === 0 ? 'N/A' : `${failPercentage}%`,
        color: theme.palette.error.main,
      },
      {
        key: 'Not Analyzed',
        value:
          insufficientPercentage === 0 ? 'N/A' : `${insufficientPercentage}%`,
        color: theme.palette.text.secondary,
      },
    ];
    const deductionBlockTitle = 'Top Deduction Codes';

    const isFourHourBlock = timeInterval === 'fourHourBlock';
    const dateRange = isFourHourBlock
      ? extractFourHourBlockDateRange(bucket.startDate)
      : formatSpikeDateRange(bucket.startDate, bucket.endDate, timeInterval);

    return {
      title: `Spike ${index + 1}`,
      spikeID: bucket.bucketID,
      totalClaims,
      vsAverage: removeDecimals((totalClaims / averageClaims) * 100),
      borderColor: spikeColors[index],
      claimData,
      date: dateRange,
      ...(isFourHourBlock && {
        subdateRange: extractFourHourBlockTimeRange(bucket.startDate),
      }),
      blocks: [
        {
          title: 'Top Sources',
          info: (
            sourceCount?.sort((a, b) => b.count - a.count).slice(0, 3) || []
          ).map(({ source, count }) => ({
            link: source,
            value: millify(count),
          })),
        },
        {
          title: deductionBlockTitle,
          info: (
            deductionCodesCount
              ?.sort((a, b) => b.count - a.count)
              .slice(0, 3) || []
          ).map(({ deductionCode, count }) => ({
            link: deductionCode,
            value: millify(count),
          })),
        },
      ],
    };
  });
};

const fileTypeMap = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  zip: 'application/zip',
};

export const generateReportCaseFile = ({
  caseName,
  todayDate,
  file,
  fileType,
}: {
  caseName: string;
  todayDate: string;
  file: string;
  fileType: 'pdf' | 'csv' | 'zip';
}) => {
  const blob = new Blob([file], {
    type: fileTypeMap[fileType],
  });
  saveAs(blob, `ClaimScore Report - ${caseName} ${todayDate}.${fileType}`);
};
